import React, { useRef } from "react"
import Tabs from "@mui/material/Tabs"
import Tab from "@mui/material/Tab"
import Tooltip from '@mui/material/Tooltip'
import { withStyles } from "@mui/styles"
import { COLOR, KEY_VALUES } from "../modules/constants"

// Minimum time to keep blocking programmatic writes to the tab bar's scrollLeft. Must outlast MUI's
// own scrollSelectedIntoView animation (theme.transitions.duration.standard is 300ms by default) -
// while our shadow (see below) is swallowing its writes, the animation produces no observable
// `scroll`/resize event to react to, so there's nothing to detect and extend against; we just have
// to wait it out.
const SCROLL_LOCK_MS = 400

// Once SCROLL_LOCK_MS has passed, how long to wait after the last detected drift (see reassert())
// before assuming the layout has actually settled and it's safe to reveal the bar again.
const SETTLE_MS = 100

// Upper bound on how long the lock can be extended past SCROLL_LOCK_MS by ongoing drift, however
// long settling actually takes (a heavier destination page - e.g. one that fetches data and
// initialises a D3 diagram on mount - can take longer to finish laying out than the lighter cases
// this was tuned against). This only guards against never seeing a quiet period at all; it is not
// expected to normally be hit.
const MAX_LOCK_MS = 2000

// Finds the object in the prototype chain that actually defines "scrollLeft", so we can borrow its
// real getter/setter - the property itself is normally defined a level or two up (Element.prototype
// in modern browsers), not as an own property of the element.
const findScrollLeftDescriptor = node => {
  for (let proto = Object.getPrototypeOf(node); proto; proto = Object.getPrototypeOf(proto)) {
    const descriptor = Object.getOwnPropertyDescriptor(proto, "scrollLeft")
    if (descriptor) return descriptor
  }
  return null
}

// Different tabs (Company/Person vs Home/Favourites etc) are rendered by entirely different page
// components (see App.jsx routes), so clicking a tab can unmount this whole component and mount a
// fresh instance for the destination page - a plain ref/state captured at click time doesn't survive
// that. Module-level state does, since the module stays loaded for the life of the SPA session, and
// there is only ever one tab bar mounted at a time.
let recentClick = null // { fraction, time }
const CLICK_GRACE_MS = 3000 // generous enough to span a route transition + data load

// Higher than KEY_VALUES.DISABLED_OPACITY so unselected/disabled tabs stay legible against the white tab bar
const TAB_DISABLED_OPACITY = "45%"

// Tints the active tab's icon/label with the brand color, rather than relying on the underline alone
const selectedTabStyles = {
  '&.Mui-selected': { color: COLOR.BRAND.STD },
}

export default withStyles({
  tabsRoot: {
    borderBottom: "1px solid #888",
    minHeight: `${KEY_VALUES.TABBAR_HEIGHT}px`,
    height: `${KEY_VALUES.TABBAR_HEIGHT}px`,
  },
  tabRootCH: {
    minWidth: KEY_VALUES.TAB_MIN_WIDTH,
    paddingBottom: 12, // padding under label
    flexGrow: 1,
    fontSize: 10, // label font size
    color: COLOR.VERY_DARK_GREY,
    ...selectedTabStyles,
  },
  tabRootCHPortrait: {
    minWidth: KEY_VALUES.TAB_MIN_WIDTH,
    paddingBottom: 12, // padding under label
    paddingLeft: 0,
    paddingRight: 0,
    flexGrow: 1,
    fontSize: 8, // smaller so 6 labels fit in portrait mode on mobile
    color: COLOR.VERY_DARK_GREY,
    ...selectedTabStyles,
  },
  tabRootXQ: {
    minWidth: KEY_VALUES.TAB_MIN_WIDTH,
    paddingBottom: 0,
    flexGrow: 1,
    fontSize: 16,
  },
  tabIndicator: { // the horizontal line under the icon+label, to indicate selected
    height: 3,
  },
  icon: {
  },
})(({ classes, options, selectedIndex, onChange, portrait }) => {
  const tabsRootRef = useRef(null)
  const settleTimer = useRef(null)
  const cleanupWatchers = useRef(null)

  // Earlier attempts fought MUI's own scroll-into-view animation frame-by-frame (repeatedly
  // resetting scrollLeft via requestAnimationFrame), but that races MUI's identical rAF-driven
  // animate() loop - whichever of the two writes scrollLeft last within a given frame wins, so the
  // position flickered between our value and MUI's mid-animation value. Instead, shadow the
  // scrollLeft property on this element so external programmatic writes (MUI's included) are simply
  // swallowed, while reads still pass through to the real value - only our own writes ever take
  // effect, so there's nothing to flicker between. Genuine user touch/wheel/scrollbar scrolling is
  // untouched, since the browser scrolls the element directly rather than going through this setter.
  //
  // The shadow only catches EXPLICIT writes though (`el.scrollLeft = x`). A layout change can also
  // move scrollLeft without going through the setter at all - if the tab bar's content reflows while
  // scrolled near the end (e.g. the freshly-selected tab's label becoming bold/wider, or its scroll
  // buttons appearing/disappearing) and the current position is no longer reachable, the browser
  // silently re-clamps it internally, bypassing the shadow entirely. That can happen on the
  // *current*, still-mounted instance too - e.g. clicking a tab that navigates to a different page
  // updates this bar's own `value` (and thus its layout) an instant before the page actually swaps
  // out from under it - not just on a freshly remounted one. Two watchers detect this regardless of
  // which instance or cause: a `scroll` listener catches every offset change (including that silent
  // re-clamp), and a ResizeObserver catches the opposite direction too - the scroller's box
  // *widening* back out (more room freed up) doesn't move the current offset at all, so it never
  // fires a `scroll` event on its own, and would otherwise be missed entirely.
  //
  // The bar is never hidden while this settles - an earlier version did hide it (visibility) for as
  // long as drift kept being detected, but a heavier destination page (e.g. one that fetches data and
  // mounts a D3 diagram) can keep nudging its own layout for several hundred ms after navigating,
  // which made the whole bar disappear for a beat instead of just not-flickering - worse than the
  // problem it was solving. Reasserting on every detected drift (see the watchers below) keeps the
  // visible position pinned to the target throughout instead.
  //
  // The target is a *fraction* of the scrollable range (0-1), not a raw pixel offset. When the click
  // that's being restored came from a different page's tab bar (a different mounted instance - see
  // below), that instance's scroll buttons can end up settling into a slightly different final state
  // than this one's (e.g. one page's bar ends up with both chevrons shown, another's with just one),
  // giving it a different scrollWidth-clientWidth max. Replaying the same raw pixel value across that
  // mismatch could fall short of - and get stuck short of - the true edge. A fraction recomputed
  // against *this* instance's own live max every time it's asserted always lands exactly on the same
  // relative position regardless of how its particular scroll buttons settle.
  const lockScrollAt = (scroller, fraction) => {
    clearTimeout(settleTimer.current)
    cleanupWatchers.current?.()

    const native = findScrollLeftDescriptor(scroller)
    if (!native) return

    const target = () => fraction * Math.max(0, scroller.scrollWidth - scroller.clientWidth)

    // The lock never unlocks before minUnlockAt, which guarantees we outlast MUI's own
    // scroll-into-view animation - while its writes are swallowed below, they produce no
    // `scroll`/resize event of their own to react to, so there's nothing marking when it's actually
    // finished; we just have to wait it out. Past that minimum, each further correction pushes the
    // unlock out by another SETTLE_MS - a heavier destination page can legitimately take longer than
    // a lighter one to finish settling - and maxLockAt is only the backstop for the (unexpected) case
    // where it never goes quiet.
    const minUnlockAt = Date.now() + SCROLL_LOCK_MS
    const maxLockAt = Date.now() + MAX_LOCK_MS

    const unlock = () => {
      cleanupWatchers.current?.()
      clearTimeout(settleTimer.current)
      delete scroller.scrollLeft // remove the own-property shadow, restoring the native accessor
    }
    const scheduleUnlock = () => {
      clearTimeout(settleTimer.current)
      const unlockAt = Math.min(Math.max(minUnlockAt, Date.now() + SETTLE_MS), maxLockAt)
      settleTimer.current = setTimeout(unlock, Math.max(0, unlockAt - Date.now()))
    }
    const reassert = () => {
      const desired = target()
      if (Math.abs(native.get.call(scroller) - desired) <= 0.5) return
      native.set.call(scroller, desired) // triggers a `scroll` event in turn, but is then a no-op above
      scheduleUnlock()
    }

    reassert()

    Object.defineProperty(scroller, "scrollLeft", {
      configurable: true,
      get: () => native.get.call(scroller),
      set: () => { }, // swallow external programmatic writes while locked
    })

    scroller.addEventListener("scroll", reassert, { passive: true })
    const ro = new ResizeObserver(reassert)
    ro.observe(scroller)

    // `scroll`/resize notifications are normally delivered before the frame that caused them paints,
    // but that's not a hard guarantee - occasionally a stray value has been observed to paint one
    // frame before either watcher reacts to it. A few explicit checks on the next few animation
    // frames closes that gap; reassert() is cheap and a no-op in the (by far most common) case where
    // nothing needs correcting, so this doesn't cost the ordinary click anything.
    let rafChecksLeft = 3
    let rafId = requestAnimationFrame(function recheck() {
      reassert()
      rafId = --rafChecksLeft > 0 ? requestAnimationFrame(recheck) : null
    })

    cleanupWatchers.current = () => {
      scroller.removeEventListener("scroll", reassert)
      ro.disconnect()
      if (rafId) cancelAnimationFrame(rafId)
    }

    // Keep blocking writes for at least SCROLL_LOCK_MS even if nothing ever drifted (see minUnlockAt
    // above for why). If the initial reassert() above already scheduled an unlock, this just
    // reconfirms the same (or, if drift is still ongoing, a later) time.
    scheduleUnlock()
  }

  const scrollFractionOf = scroller => {
    const max = scroller.scrollWidth - scroller.clientWidth
    return max > 0 ? scroller.scrollLeft / max : 0
  }

  React.useEffect(() => () => {
    clearTimeout(settleTimer.current)
    cleanupWatchers.current?.()
  }, [])

  // If this instance mounted because a tab click just navigated to a different page (see App.jsx
  // routes - Company/Person, Home/Favourites etc are different top-level page components, so the
  // tab bar itself remounts), immediately restore and re-lock the scroll position the previous
  // instance had, before the new instance's own mount-time scrollSelectedIntoView can move it.
  React.useLayoutEffect(() => {
    if (!portrait || !recentClick || Date.now() - recentClick.time > CLICK_GRACE_MS) return
    const scroller = tabsRootRef.current?.querySelector(".MuiTabs-scroller")
    if (scroller) lockScrollAt(scroller, recentClick.fraction)
  }, [])

  // In portrait, the tab bar is horizontally scrollable and the user often scrolls it manually
  // to reach a tab. MUI's Tabs then auto-scrolls the newly selected tab into view on click, which
  // yanks the bar away from where the user just was and hides the tab they picked - confusing.
  // We lock the scroll position at its pre-click value for as long as MUI's own scroll animation
  // could still be running, which cancels it out without touching MUI's internals.
  const handleChange = (event, newValue) => {
    const scroller = portrait && tabsRootRef.current?.querySelector(".MuiTabs-scroller")
    if (scroller) {
      recentClick = { fraction: scrollFractionOf(scroller), time: Date.now() }
      lockScrollAt(scroller, recentClick.fraction)
    }

    onChange(newValue)
  }

  return (
    <Tabs
      ref={tabsRootRef}
      classes={{ root: classes.tabsRoot, indicator: classes.tabIndicator }}
      indicatorColor="primary"
      textColor="inherit"
      value={selectedIndex}
      onChange={handleChange}
      variant={portrait ? "scrollable" : "standard"}
      // "auto" (rather than always-on) mounts/unmounts the chevrons as scroll position changes,
      // which changes the scroller's own clientWidth right when a page-changing click is also
      // trying to restore its scroll position (see lockScrollAt below) - always rendering them
      // (just inactive/greyed when there's nothing to scroll that direction) keeps that width
      // stable throughout, removing a whole class of transient drift at the source.
      scrollButtons={portrait ? true : false}
      allowScrollButtonsMobile>

      {options.map((option, index) => {
        const selected = index === selectedIndex

        return (
          <Tooltip
            key={option.key}
            // disableHoverListener={!tooltipTitle}
            title={option.tooltipTitle}
          >
            <Tab
              key={option.key}
              classes={option.icon || option.image
                ? portrait
                  ? { root: classes.tabRootCHPortrait }
                  : { root: classes.tabRootCH }
                : { root: classes.tabRootXQ }}
              sx={{
                minHeight: `${KEY_VALUES.TABBAR_HEIGHT}px`,
                ...(option.icon || option.image ? {
                  paddingTop: '9px',
                  paddingBottom: '1px',
                  '& .MuiTab-iconWrapper': { marginBottom: '10px' },
                } : {}),
              }}
              style={{
                fontSize: option.icon || option.image ? (portrait ? 8 : 10) : 16,
                opacity: option.disabled ? TAB_DISABLED_OPACITY : "100%",
                ...(option.leftLine ? { borderLeft: option.disabled ? `1px solid ${COLOR.MID_GREY}` : "1px solid #CCC" } : {}),
              }}
              label={option.label}
              icon={option.icon ||
                (option.image && (selected
                  // Recolor via mask rather than a CSS filter, so the glyph is always exactly
                  // COLOR.BRAND.STD (which itself varies by environment) instead of an approximated tint
                  ? <span
                    role="img"
                    aria-label={option.label}
                    style={{
                      display: "inline-block",
                      width: 40,
                      height: 24,
                      marginTop: 4,
                      marginBottom: 2,
                      backgroundColor: COLOR.BRAND.STD,
                      WebkitMaskImage: `url(${option.image})`,
                      maskImage: `url(${option.image})`,
                      WebkitMaskRepeat: "no-repeat",
                      maskRepeat: "no-repeat",
                      WebkitMaskPosition: "center",
                      maskPosition: "center",
                      WebkitMaskSize: "contain",
                      maskSize: "contain",
                    }}
                  />
                  : <img alt={option.label} src={option.image} style={{ height: 24, marginTop: 4, marginBottom: 2, opacity: 0.75 }} />)
                )
                // opacity value should match text & icon color
              }
            />
          </Tooltip>
        )
      })}
    </Tabs>
  )
}
)
